import { define } from 'wicked-elements'
import Bowser from 'bowser'

const INSTALL_PROMPT_DELAY = 0
const PWA_PROMPT_KEY = 'gpla-prompt:pwa'

define('[data-component="install-prompt"]', {
  mobileOnly: true, // Set to false to allow on all devices
  instructionsOpen: false,
  message: '',

  init() {
    this.deferredPrompt = null
    this.isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.matchMedia('(display-mode: minimal-ui)').matches
      || window.navigator.standalone
    this.hasShownPrompt = localStorage.getItem(PWA_PROMPT_KEY) === 'true'
    this.message = this.element.dataset?.message || ''
    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault()
      this.deferredPrompt = e
      if (!this.isIOS() && this.shouldShowBanner()) {
        this.showInstallBannerWithDelay()
      }
    })
    // For iOS, show banner immediately if conditions are met
    if (this.isIOS() && this.shouldShowBanner()) {
      this.showInstallBannerWithDelay()
    }
    // For Safari desktop, show banner if eligible
    if (!this.mobileOnly && this.isSafariDesktop() && this.shouldShowBanner()) {
      this.showInstallBannerWithDelay()
    }
  },

  getBrowserInfo() {
    if (!this._browserInfo) {
      this._browserInfo = Bowser.getParser(window.navigator.userAgent)
    }
    return this._browserInfo
  },

  isMobileDevice() {
    const browser = this.getBrowserInfo()
    const platform = browser.getPlatformType()
    return platform === 'mobile' || platform === 'tablet'
  },

  isIOS() {
    const browser = this.getBrowserInfo()
    return browser.getOSName() === 'iOS'
  },

  isSafariDesktop() {
    const browser = this.getBrowserInfo()
    return browser.getBrowserName() === 'Safari' && browser.getPlatformType() === 'desktop' && browser.getOSName() === 'macOS'
  },

  shouldShowBanner() {
    if (this.isStandalone || this.hasShownPrompt) return false
    if (this.isIOS() && this.isMobileDevice()) return true
    if (!this.isIOS() && this.deferredPrompt) {
      if (this.mobileOnly) {
        return this.isMobileDevice()
      }
      return true
    }
    // Safari desktop support
    if (!this.mobileOnly && this.isSafariDesktop()) {
      return true
    }
    return false
  },

  showInstallBannerWithDelay() {
    setTimeout(() => {
      if (this.shouldShowBanner()) {
        this.renderBanner()
      }
    }, INSTALL_PROMPT_DELAY)
  },

  renderBanner() {
    if (this.isIOS()) {
      this.renderIOSBanner()
    } else if (this.isSafariDesktop()) {
      this.renderSafariDesktopBanner()
    } else {
      this.renderNativeBanner()
    }
  },

  primaryBanner({
    primaryLabel = 'Install App',
    primaryIcon = ''
  } = {}) {
    return `
      <button class="absolute top-0 right-0 p-3 text-xl leading-none" data-dismiss>×</button>
      <div class="flex flex-col gap-x-8 gap-y-4">
        <div class="w-full flex items-center gap-3 pointer-events-none select-none">
          <div class="w-11 h-11 bg-yellow border rounded-lg flex-none self-start">
            <img src="/assets/images/pwa/icon_preview.svg" alt="GP.LA" class="w-full h-full" />
          </div>
          <div class="pt-0.5 text-lg">
            <h3 class="font-bold">Install GP.LA</h3>
            <p>${this.message}</p>
          </div>
        </div>
        <div class="w-full flex gap-3">
          <button
            class="flex-1 ui-button ui-button--sm h-11 text-base flex items-center justify-center relative bg-white hover:bg-yellow"
            data-primary
          >
            <span class="absolute left-0 right-0 text-center w-full">${primaryLabel}</span>
            ${primaryIcon ? `<span class="ml-auto text-lg">${primaryIcon}</span>` : ''}
          </button>
          <button class="flex-1 ui-button ui-button--sm h-11 text-base border-neutral-400 hover:border-black transition-colors" data-secondary>Maybe Later</button>
        </div>
      </div>
    `
  },

  renderIOSBanner() {
    const icon = this.instructionsOpen ? '−' : '+';
    const primaryLabel = this.instructionsOpen ? 'Hide' : 'Install App';
    const instructionsClass = this.instructionsOpen ? '' : 'hidden';
    const instructions = `
      <div class="install-instructions ${instructionsClass} mt-6 text-lg">
        <ol class="text-left space-y-1 mb-4">
          <li><span class="mr-2">1.</span> Tap the <span class="ui-pill text-xs inline-block align-top !opacity-100">Share</span> button in Safari</li>
          <li><span class="mr-2">2.</span> Scroll down and tap <span class="ui-pill text-xs inline-block align-top !opacity-100">Add to Home Screen</span></li>
          <li><span class="mr-2">3.</span> Tap <span class="ui-pill text-xs inline-block align-top !opacity-100">Add</span> to install</li>
        </ol>
        <p class="text-base text-neutral-500">The app will appear on your home screen like any other app.</p>
      </div>
    `;
    this.element.innerHTML = `
      <div class="bg-neutral-50 border border-black p-3 relative rounded-lg">
        ${this.primaryBanner({
          primaryLabel,
          primaryIcon: icon
        })}
        ${instructions}
      </div>
    `
    this.element.querySelector('[data-primary]').addEventListener('click', () => this.toggleInstructions())
    this.element.querySelector('[data-secondary]').addEventListener('click', () => this.dismissPrompt())
    this.element.querySelector('[data-dismiss]').addEventListener('click', () => this.dismissPrompt())
  },

  renderSafariDesktopBanner() {
    const icon = this.instructionsOpen ? '−' : '+';
    const primaryLabel = this.instructionsOpen ? 'Hide' : 'Install App';
    const instructionsClass = this.instructionsOpen ? '' : 'hidden';
    const instructions = `
      <div class="install-instructions ${instructionsClass} mt-6 text-lg">
        <ol class="space-y-1 mb-4">
          <li><span class="mr-2">1.</span> Open Safari’s <span class="ui-pill text-xs inline-block align-top !opacity-100">File</span> menu</li>
          <li><span class="mr-2">2.</span> Click <span class="ui-pill text-xs inline-block align-top !opacity-100">Add to Dock…</span></li>
          <li><span class="mr-2">3.</span> Follow the prompts to install</li>
        </ol>
        <p class="text-base text-neutral-500">The app will appear in your Dock like any other app.</p>
      </div>
    `;
    this.element.innerHTML = `
      <div class="bg-neutral-50 border border-black p-3 relative rounded-lg">
        ${this.primaryBanner({
          primaryLabel,
          primaryIcon: icon
        })}
        ${instructions}
      </div>
    `
    this.element.querySelector('[data-primary]').addEventListener('click', () => this.toggleInstructions())
    this.element.querySelector('[data-secondary]').addEventListener('click', () => this.dismissPrompt())
    this.element.querySelector('[data-dismiss]').addEventListener('click', () => this.dismissPrompt())
  },

  renderNativeBanner() {
    this.element.innerHTML = `
      <div class="bg-neutral-50 border border-black p-3 relative rounded-lg">
        ${this.primaryBanner({
          primaryLabel: 'Install App',
          primaryIcon: ''
        })}
      </div>
    `
    this.element.querySelector('[data-primary]').addEventListener('click', () => this.handleInstallClick())
    this.element.querySelector('[data-secondary]').addEventListener('click', () => this.dismissPrompt())
    this.element.querySelector('[data-dismiss]').addEventListener('click', () => this.dismissPrompt())
  },

  toggleInstructions() {
    this.instructionsOpen = !this.instructionsOpen;
    this.renderBanner();
  },

  handleInstallClick() {
    if (this.deferredPrompt) {
      this.installApp()
    } else {
      this.dismissPrompt()
    }
  },

  async installApp() {
    if (!this.deferredPrompt) return
    try {
      const result = await this.deferredPrompt.prompt()
      if (result.outcome === 'accepted') {
        this.trackInstall('accepted')
        this.dismissPrompt()
      } else {
        this.trackInstall('dismissed')
        // Do NOT dismiss the banner, let the user try again
      }
    } catch (error) {
      console.error('Install prompt error:', error)
    }
    this.deferredPrompt = null
  },

  dismissPrompt() {
    localStorage.setItem(PWA_PROMPT_KEY, 'true')
    this.hasShownPrompt = true
    this.element.innerHTML = ''
  },

  trackInstall(outcome) {
    if (window.gtag) {
      window.gtag('event', 'pwa_install_prompt', {
        event_category: 'PWA',
        event_label: outcome
      })
    }
  }
}) 